if (!customElements.get('leslis-product-assistance')) {
  customElements.define('leslis-product-assistance', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.frame = null;
      this.preparedDialogs = new WeakSet();
      this.bar = this.querySelector('[data-assistance-bar]');
      this.mobile = window.matchMedia('(max-width: 749px)');
      const options = { signal: this.controller.signal };
      const canUseDialog = typeof HTMLDialogElement !== 'undefined' && 'showModal' in HTMLDialogElement.prototype;
      if (canUseDialog) {
        this.querySelectorAll('[data-assistance-enhanced]').forEach((item) => { item.hidden = false; });
        this.querySelectorAll('[data-assistance-fallback]').forEach((item) => { item.hidden = true; });
      }
      this.addEventListener('click', (event) => this.handleClick(event), options);
      if (this.closest('product-info[id^="MainProduct-"]')) {
        document.addEventListener('keydown', (event) => this.jumpToReviews(event), { ...options, capture: true });
        document.addEventListener('click', (event) => {
          const opener = event.target.closest?.('main .jdgm-review-widget .jdgm-rev__read-full[href]');
          if (opener && this.isVisible(opener)) opener.focus({ preventScroll: true });
        }, { ...options, capture: true });
        document.addEventListener('keydown', (event) => {
          if (event.key !== ' ' || event.defaultPrevented || event.isComposing || event.ctrlKey || event.altKey || event.metaKey) return;
          const opener = event.target.closest?.('main .jdgm-review-widget .jdgm-rev__read-full[href][role="button"]');
          if (!opener || !this.isVisible(opener)) return;
          event.preventDefault();
          if (!event.repeat) opener.click();
        }, options);
        this.observeReviewRegions();
        this.mobile.addEventListener('change', () => this.updateReviewReadingState(), options);
      }
      if (this.bar) {
        window.addEventListener('scroll', () => this.scheduleUpdate(), { ...options, passive: true });
        window.addEventListener('resize', () => this.scheduleUpdate(), { ...options, passive: true });
        window.visualViewport?.addEventListener('resize', () => this.scheduleUpdate(), options);
        document.addEventListener('focusin', () => this.scheduleUpdate(), options);
        document.addEventListener('focusout', () => this.scheduleUpdate(), options);
        document.addEventListener('product-info:loaded', () => this.scheduleUpdate(), options);
        window.addEventListener('CookiebotOnDialogDisplay', () => this.scheduleUpdate(), options);
        window.addEventListener('CookiebotOnDialogHide', () => this.scheduleUpdate(), options);
        this.scheduleUpdate();
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.reviewObserver?.disconnect();
      if (this.reviewRegions) document.body.removeAttribute('data-leslis-reading-reviews');
      if (this.frame !== null) cancelAnimationFrame(this.frame);
      this.querySelectorAll('dialog[open]').forEach((dialog) => dialog.close());
    }

    prepareDialog(dialog) {
      if (this.preparedDialogs.has(dialog)) return;
      this.preparedDialogs.add(dialog);
      const options = { signal: this.controller.signal };
      dialog.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab') return;
        const controls = Array.from(dialog.querySelectorAll('button:not(:disabled), a[href], input:not(:disabled):not([type="hidden"]), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]')).filter((control) => this.isVisible(control));
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (!first) return;
        if (event.shiftKey && (document.activeElement === first || !controls.includes(document.activeElement))) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }, options);
      dialog.addEventListener('close', () => {
        if (this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
        this.updateBarNow();
      }, options);
      dialog.addEventListener('click', (event) => {
        const rect = dialog.getBoundingClientRect();
        if (event.target === dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom)) dialog.close();
      }, options);
    }

    handleClick(event) {
      const openButton = event.target.closest('[data-assistance-open]');
      if (openButton && this.contains(openButton)) {
        const dialog = document.getElementById(openButton.dataset.assistanceOpen);
        if (!dialog || dialog.open || typeof dialog.showModal !== 'function') return;
        this.prepareDialog(dialog);
        this.returnFocus = openButton;
        dialog.showModal();
        dialog.querySelector('h2')?.focus({ preventScroll: true });
        this.updateBarNow();
        return;
      }
      const closeButton = event.target.closest('[data-assistance-close]');
      if (closeButton && this.contains(closeButton)) {
        closeButton.closest('dialog')?.close();
        return;
      }
      if (event.target.closest('[data-assistance-return]')) this.returnToSelection();
    }

    jumpToReviews(event) {
      if (!['Enter', ' '].includes(event.key) || event.repeat || event.defaultPrevented || event.isComposing || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      const opener = event.target.closest?.('main .jdgm-preview-badge .jdgm-prev-badge__stars[role="button"]');
      if (!opener || document.activeElement !== opener || opener.closest('.jdgm-preview-badge')?.dataset.id !== this.dataset.productId) return;
      const heading = document.querySelector('main #judgeme_product_reviews h2');
      if (!heading || !this.isVisible(heading)) return;
      event.preventDefault();
      event.stopPropagation();
      if (!heading.hasAttribute('tabindex')) {
        heading.setAttribute('tabindex', '-1');
        heading.addEventListener('blur', () => {
          if (heading.getAttribute('tabindex') === '-1') heading.removeAttribute('tabindex');
        }, { once: true });
      }
      const header = document.querySelector('.shopify-section-header-sticky');
      const headerRect = header?.getBoundingClientRect();
      const headerSpace = Math.max(0, headerRect?.bottom || 0, headerRect?.height || 0);
      const offset = Math.min(headerSpace, 160) + 24;
      window.scrollTo({ top: window.scrollY + heading.getBoundingClientRect().top - offset, behavior: 'instant' });
      heading.focus({ preventScroll: true });
      this.updateBarNow();
    }

    observeReviewRegions() {
      this.reviewRegions = new Set();
      if (typeof IntersectionObserver === 'undefined') return;
      this.reviewObserver = new IntersectionObserver((entries, observer) => {
        if (!this.isConnected || observer !== this.reviewObserver) return;
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRect.height > 0) this.reviewRegions.add(entry.target);
          else this.reviewRegions.delete(entry.target);
        });
        this.updateReviewReadingState();
      });
      const regions = new Set(Array.from(document.querySelectorAll('main #judgeme_product_reviews, main .jdgm-all-reviews-text')).map((region) => region.closest('.shopify-section') || region));
      regions.forEach((region) => this.reviewObserver.observe(region));
    }

    updateReviewReadingState() {
      document.body.toggleAttribute('data-leslis-reading-reviews', this.mobile.matches && this.reviewRegions.size > 0);
    }

    purchaseRegion() {
      const containingRegion = this.closest('product-info[id^="MainProduct-"]');
      if (containingRegion) return containingRegion;
      return Array.from(document.querySelectorAll('product-info[id^="MainProduct-"]')).find((region) => region.dataset.productId === this.dataset.productId && region.getClientRects().length);
    }

    purchaseButton(region) {
      return region?.querySelector('product-form button[name="add"]');
    }

    isVisible(control) {
      return control.getClientRects().length > 0 && getComputedStyle(control).visibility !== 'hidden';
    }

    requiredOption(region) {
      const controls = Array.from(region.querySelectorAll('input[required], select[required], textarea[required], [aria-required="true"], [aria-invalid="true"], .gpo-element input[presence], .gpo-element select[presence], .gpo-element textarea[presence]'));
      const gpoControls = Array.from(region.querySelectorAll('.gpo-element label.gpo-label.required')).map((label) => label.control).filter((control) => control && region.contains(control));
      return Array.from(new Set([...controls, ...gpoControls])).find((control) => {
        if (control.disabled || control.type === 'hidden' || !this.isVisible(control)) return false;
        if (control.getAttribute('aria-invalid') === 'true' || control.validity?.valid === false) return true;
        const gpoRequired = control.closest('.gpo-element') && (control.hasAttribute('presence') || Array.from(control.labels || []).some((label) => label.matches('.gpo-label.required')));
        if (!control.required && control.getAttribute('aria-required') !== 'true' && !gpoRequired) return false;
        if (control.type === 'radio') return !Array.from(region.querySelectorAll('input[type="radio"]')).some((radio) => radio.name === control.name && radio.checked);
        if (control.type === 'checkbox') return !control.checked;
        return 'value' in control && !String(control.value).trim();
      });
    }

    returnToSelection() {
      const region = this.purchaseRegion();
      if (!region) return;
      const button = this.purchaseButton(region);
      const required = this.requiredOption(region);
      const explicitOptions = region.querySelector('[data-leslis-required-options]');
      const explicitControl = explicitOptions ? Array.from(explicitOptions.querySelectorAll('input:not([type="hidden"]):not(:disabled), select:not(:disabled), textarea:not(:disabled), button:not(:disabled), [tabindex="0"]')).find((control) => this.isVisible(control)) : null;
      let target = required || (explicitControl && this.isVisible(explicitControl) ? explicitControl : null);
      if (!target && button && !button.disabled) target = button;
      if (!target) target = region.querySelector('variant-selects input:not(:disabled), variant-selects select:not(:disabled)') || region;
      if (!target.matches('input, select, textarea, button, a[href], [tabindex]')) {
        target.setAttribute('tabindex', '-1');
        target.addEventListener('blur', () => target.removeAttribute('tabindex'), { once: true });
      }
      const header = document.querySelector('.shopify-section-header-sticky');
      const headerRect = header?.getBoundingClientRect();
      const headerSpace = Math.max(0, headerRect?.bottom || 0, headerRect?.height || 0);
      const offset = Math.min(headerSpace, 160) + 24;
      window.scrollTo({ top: window.scrollY + target.getBoundingClientRect().top - offset, behavior: 'instant' });
      target.focus({ preventScroll: true });
      this.updateBarNow();
    }

    scheduleUpdate() {
      if (this.frame !== null) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.updateBar();
      });
    }

    updateBarNow() {
      if (this.frame !== null) cancelAnimationFrame(this.frame);
      this.frame = null;
      if (!this.isConnected) return;
      this.updateBar();
    }

    updateBar() {
      if (!this.bar) return;
      const active = document.activeElement;
      const editing = active && /^(INPUT|SELECT|TEXTAREA)$/.test(active.tagName);
      const keyboardOpen = window.visualViewport && window.visualViewport.height < window.innerHeight * 0.75;
      const ownDialogOpen = this.querySelector('dialog[open]');
      const consentBanner = document.getElementById('CybotCookiebotDialog');
      const consentOpen = consentBanner && this.isVisible(consentBanner);
      if (!this.mobile.matches || editing || keyboardOpen || ownDialogOpen || consentOpen) {
        this.bar.hidden = true;
        return;
      }
      const button = this.purchaseButton(this.purchaseRegion());
      if (!button || button.getBoundingClientRect().bottom >= 0 || !this.isVisible(button)) {
        this.bar.hidden = true;
        return;
      }
      const footer = document.querySelector('.shopify-section-group-footer-group');
      const footerVisible = footer && footer.getBoundingClientRect().top < window.innerHeight;
      this.bar.hidden = Boolean(footerVisible);
    }
  });
}
